import { CONFIRM_TEXT } from '@/shared/config/confirm-text'
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  InfoCircleFilled,
  WarningFilled,
} from '@/shared/ui/icons'
import { Modal, type ModalFuncProps } from 'antd'
import DOMPurify from 'dompurify'
import parse from 'html-react-parser'
import type React from 'react'

interface UiConfirmModalProps extends Omit<ModalFuncProps, 'content'> {
  width?: number
  title?: string
  titleHtml?: string | React.ReactNode
  icon?: string
  okText?: string
  cancelText?: string
  // biome-ignore lint/suspicious/noConfusingVoidType: AntD Modal API cho phép void hoặc Promise
  onOk?: (e?: React.MouseEvent<HTMLElement>) => void | Promise<unknown>
  onCancel?: (e?: React.MouseEvent<HTMLElement>) => void
  showCancel?: boolean
  okButtonProps?: ModalFuncProps['okButtonProps']
  warningTitle?: boolean
}

const renderIcon = (icon?: string) => {
  if (!icon)
    return (
      <ExclamationCircleFilled
        style={{ color: 'var(--vbdt-color-status-warning)', fontSize: 24 }}
      />
    )
  if (icon === 'trashRed' || icon === 'warning' || icon === 'warning-usb') {
    return <WarningFilled style={{ color: 'var(--vbdt-color-status-error)', fontSize: 24 }} />
  }
  if (icon === 'info') {
    return <InfoCircleFilled style={{ color: 'var(--vbdt-color-status-info)', fontSize: 24 }} />
  }
  if (icon === 'success') {
    return <CheckCircleFilled style={{ color: 'var(--vbdt-color-status-success)', fontSize: 24 }} />
  }
  return (
    <ExclamationCircleFilled style={{ color: 'var(--vbdt-color-status-warning)', fontSize: 24 }} />
  )
}

export default function UiConfirmModal({
  width = 440,
  title,
  titleHtml = '',
  icon = 'warning',
  okText = CONFIRM_TEXT.ok,
  cancelText = CONFIRM_TEXT.cancel,
  onOk,
  onCancel,
  okButtonProps = {},
  warningTitle = false,
  ...props
}: UiConfirmModalProps): void {
  const isDangerConfirm =
    warningTitle || icon === 'trashRed' || icon === 'warning' || icon === 'warning-usb'

  Modal.confirm({
    icon: null,
    okText,
    cancelText,
    width,
    onOk,
    onCancel,
    centered: true,
    mask: { closable: true },
    autoFocusButton: 'ok',
    okButtonProps: {
      type: 'primary',
      danger: isDangerConfirm,
      ...(isDangerConfirm ? { color: 'danger' } : {}),
      ...okButtonProps,
    },
    cancelButtonProps: {
      ...props.cancelButtonProps,
    },
    ...props,
    content: (
      <div
        style={{
          display: 'flex',
          gap: 16,
          alignItems: 'flex-start',
          paddingTop: 'var(--vbdt-space-xs)',
        }}
      >
        <div style={{ flexShrink: 0, marginTop: 2 }}>{renderIcon(icon)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {!!title && (
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: warningTitle
                  ? 'var(--vbdt-color-status-error)'
                  : 'var(--vbdt-color-text-primary)',
                marginBottom: titleHtml ? 8 : 0,
                lineHeight: 1.4,
              }}
            >
              {parse(DOMPurify.sanitize(title))}
            </div>
          )}
          {titleHtml && (
            <div
              style={{
                fontSize: 14,
                color: 'var(--vbdt-color-text-secondary)',
                lineHeight: 1.5,
              }}
            >
              {typeof titleHtml === 'string' ? parse(DOMPurify.sanitize(titleHtml)) : titleHtml}
            </div>
          )}
        </div>
      </div>
    ),
  })
}
